import { Injectable, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AccountCompanyService } from './account-company.service';
import { AccountingCompanyContextService } from './accounting-company-context.service';
import { MasterDataCacheService } from './master-data-cache.service';
import { AccountCompanyWiseConfigService } from './account-company-wise-config.service';
import {
  LoginCompanyDto,
  SelectCompanyResponse,
} from '../models/dtos/auth.dto';
import { AccountCompanyDto } from '../models/dtos/account-company.dto';
import { ApiResponse } from './api.service';

export interface CurrentCompanySnapshot {
  companyId: string;
  companyName: string;
  mailingName?: string | null;
}

/** Result of post-login company resolution (auto-select vs modal). */
export type LoginCompanySelectionResult = 'selected' | 'modal' | 'failed';

@Injectable({ providedIn: 'root' })
export class CurrentCompanyService {
  private readonly auth = inject(AuthService);
  private readonly accountCompanyService = inject(AccountCompanyService);
  private readonly companyContext = inject(AccountingCompanyContextService);
  private readonly masterDataCache = inject(MasterDataCacheService);
  private readonly companyWiseConfig = inject(AccountCompanyWiseConfigService);
  private readonly router = inject(Router);

  private readonly currentCompanySubject =
    new BehaviorSubject<CurrentCompanySnapshot | null>(null);

  /** Active company for header UI and subscribers. */
  readonly currentCompany$ = this.currentCompanySubject.asObservable();

  constructor() {
    effect(() => {
      const dto = this.companyContext.company();
      if (!dto?.accountCompanyId) {
        return;
      }

      const next = this.toSnapshot(dto);
      const current = this.currentCompanySubject.value;
      if (
        current?.companyId?.toLowerCase() === next.companyId.toLowerCase() &&
        current.companyName === next.companyName
      ) {
        return;
      }

      this.currentCompanySubject.next(next);
    });
  }

  get snapshot(): CurrentCompanySnapshot | null {
    return this.currentCompanySubject.value;
  }

  /** Clears in-memory company state on logout without touching persisted auth storage. */
  clearSession(): void {
    this.currentCompanySubject.next(null);
  }

  getAvailableCompanies(): LoginCompanyDto[] {
    return this.auth.getLoginCompanies();
  }

  /**
   * After login (or when a company is required but not open): auto-select when the
   * user has exactly one eligible company; otherwise open the login selection modal.
   */
  resolveLoginCompanySelection(
    returnUrl?: string | null,
  ): Observable<LoginCompanySelectionResult> {
    const companies = this.getAvailableCompanies();

    if (companies.length === 0) {
      this.companyContext.openLoginCompanySelectionModal(returnUrl);
      return of('modal');
    }

    if (companies.length === 1) {
      return this.autoSelectLoginCompany(companies[0].companyId, returnUrl);
    }

    this.companyContext.openLoginCompanySelectionModal(returnUrl);
    return of('modal');
  }

  /** Sync BehaviorSubject from loaded accounting company context. */
  syncFromContext(): void {
    const dto = this.companyContext.company();
    if (!dto?.accountCompanyId) {
      return;
    }

    this.currentCompanySubject.next(this.toSnapshot(dto));
  }

  /**
   * Validates and applies a company switch (login selection or header dropdown).
   * Clears caches, refreshes company context, and navigates to the gateway.
   */
  switchCompany(companyId: string): Observable<boolean> {
    const normalized = companyId.trim().toLowerCase();
    const current = this.currentCompanySubject.value?.companyId?.trim().toLowerCase();
    if (current && current === normalized) {
      return of(true);
    }

    return this.auth.selectCompany(companyId).pipe(
      switchMap((response) => this.applySelectionResponse(response)),
      tap((success) => {
        if (success) {
          void this.router.navigate(['/accounting']);
        }
      }),
    );
  }

  /**
   * Applies company selection after login without forcing gateway navigation.
   */
  applyLoginSelection(companyId: string): Observable<boolean> {
    const normalized = companyId.trim().toLowerCase();
    const current = this.currentCompanySubject.value?.companyId?.trim().toLowerCase();
    if (current && current === normalized) {
      return of(true);
    }

    return this.auth.selectCompany(companyId).pipe(
      switchMap((response) => this.applySelectionResponse(response)),
    );
  }

  private autoSelectLoginCompany(
    companyId: string,
    returnUrl?: string | null,
  ): Observable<LoginCompanySelectionResult> {
    return this.applyLoginSelection(companyId).pipe(
      map((success) => {
        if (success) {
          this.companyContext.completeLoginCompanySelection();
          this.companyContext.ensureLoaded(true);
          return 'selected' as const;
        }
        this.companyContext.openLoginCompanySelectionModal(returnUrl);
        return 'modal' as const;
      }),
      catchError(() => {
        this.companyContext.openLoginCompanySelectionModal(returnUrl);
        return of('modal' as const);
      }),
    );
  }

  private applySelectionResponse(
    response: ApiResponse<SelectCompanyResponse | null>,
  ): Observable<boolean> {
    if (!response.success || !response.data) {
      return of(false);
    }

    return this.applySelectedCompany(response.data);
  }

  private applySelectedCompany(
    selection: SelectCompanyResponse,
  ): Observable<boolean> {
    this.refreshCachedData();

    this.accountCompanyService.setActiveCompanyId(selection.companyId);
    this.accountCompanyService.markCompanyOpen();
    this.currentCompanySubject.next({
      companyId: selection.companyId,
      companyName: selection.companyName,
      mailingName: selection.mailingName,
    });

    return this.accountCompanyService.getCompanyById(selection.companyId).pipe(
      map((loadRes) => {
        if (loadRes.success && loadRes.data) {
          // Always push full company DTO into context (name + details for gateway UI).
          this.companyContext.switchToCompany(loadRes.data);
          this.currentCompanySubject.next(this.toSnapshot(loadRes.data));
          this.syncFromContext();
          return true;
        }

        this.companyContext.switchToCompany(this.toMinimalDto(selection));
        this.syncFromContext();
        return true;
      }),
    );
  }

  private refreshCachedData(): void {
    this.masterDataCache.clearAll();
    this.companyWiseConfig.clearCache();
  }

  private toSnapshot(dto: AccountCompanyDto): CurrentCompanySnapshot {
    return {
      companyId: dto.accountCompanyId ?? '',
      companyName: dto.companyName?.trim() || 'Company',
      mailingName: dto.mailingName,
    };
  }

  private toMinimalDto(selection: SelectCompanyResponse): AccountCompanyDto {
    return {
      accountCompanyId: selection.companyId,
      companyName: selection.companyName,
      mailingName: selection.mailingName ?? null,
      accountStateId: null,
      accountCountryId: null,
      stateName: '',
      countryName: '',
      financialYearStart: '',
      booksBeginningFrom: '',
      currencySymbol: '₹',
      currencyFormalName: 'INR',
      isActive: selection.isActive,
    };
  }
}
